import type { CSSProperties } from 'react';

import { linesGridHandlers, type CellSetter } from './linesGrid';

/**
 * 엑셀처럼 줄 단위로 적는 표 (DOC-122 §18 · 사용자 지시 2026-10-23 「엑셀표처럼 쓰는 곳은 왼쪽이나 외부로」).
 *
 * <p>칸은 테두리로 이어진 격자이고 칸 안이 곧 입력칸이다(둥근 상자가 아니다). 맨 끝에는 늘 빈 줄 하나가 서 있어
 * 바로 이어 적는다 — 저장 목록에는 그 빈 줄을 넣지 않는다. Enter 는 아래 칸으로, 마지막 줄이면 새 줄로 간다.
 * 엑셀에서 여러 줄 · 여러 칸을 복사해 붙이면 커서가 있던 칸부터 채운다({@link linesGridHandlers}).
 */
export type SheetColumn<T> = {
  label: string;
  /** 폭(%, px …). 비우면 남는 폭을 나눈다. */
  width?: string;
  kind?: 'text' | 'number' | 'check';
  get: (row: T) => string | number | boolean | null | undefined;
  set: (row: T, value: string | boolean) => T;
};

export function SheetGrid<T>({
  title,
  columns,
  rows,
  onChange,
  blank,
  isBlank,
}: {
  title: string;
  columns: SheetColumn<T>[];
  rows: T[];
  onChange: (rows: T[]) => void;
  blank: () => T;
  isBlank: (row: T) => boolean;
}) {
  // 끝의 빈 줄들은 저장 목록에 넣지 않는다 — 화면에만 하나 세운다.
  const trim = (list: T[]) => {
    const next = [...list];
    while (next.length > 0 && isBlank(next[next.length - 1]!)) next.pop();
    return next;
  };
  const data = trim(rows);
  const shown = [...data, blank()];
  const commit = (next: T[]) => onChange(trim(next));
  const setters: CellSetter<T>[] = columns.map((column) =>
    column.kind === 'check' ? (row, v) => column.set(row, /^(o|○|v|✓|✔|1|y|예|true|이행|확인)$/i.test(v)) : (row, v) => column.set(row, v),
  );

  return (
    <div>
      <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>{title}</div>
      <table style={sheet}>
        <colgroup>
          <col style={{ width: '26px' }} />
          {columns.map((column) => (
            <col key={column.label} style={column.width ? { width: column.width } : undefined} />
          ))}
          <col style={{ width: '22px' }} />
        </colgroup>
        <thead>
          <tr>
            <th style={head} />
            {columns.map((column) => (
              <th key={column.label} style={head}>
                {column.label}
              </th>
            ))}
            <th style={head} />
          </tr>
        </thead>
        <tbody {...linesGridHandlers(shown, commit, blank, setters)}>
          {shown.map((row, index) => {
            const last = index === shown.length - 1;
            return (
              <tr key={index}>
                <td style={{ ...cell, ...no }}>{index + 1}</td>
                {columns.map((column) => {
                  const value = column.get(row);
                  const update = (v: string | boolean) => commit(shown.map((one, at) => (at === index ? column.set(one, v) : one)));
                  if (column.kind === 'check') {
                    return (
                      <td key={column.label} style={{ ...cell, textAlign: 'center' }}>
                        <input type="checkbox" aria-label={column.label} checked={!!value} onChange={(e) => update(e.target.checked)} />
                      </td>
                    );
                  }
                  return (
                    <td key={column.label} style={cell}>
                      <input
                        className="sj-sheet-cell"
                        type={column.kind === 'number' ? 'number' : 'text'}
                        min={column.kind === 'number' ? 0 : undefined}
                        style={input}
                        value={value === null || value === undefined ? '' : String(value)}
                        onChange={(e) => update(e.target.value)}
                      />
                    </td>
                  );
                })}
                <td style={{ ...cell, ...no }}>
                  {!last && (
                    <button type="button" aria-label="줄 삭제" style={remove} onClick={() => commit(shown.filter((_, at) => at !== index))}>
                      ×
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** 칸 안 입력의 초점 표시 — 둥근 상자 대신 칸 테두리 안쪽 선. 패널에 한 번 심는다. */
export const SHEET_STYLES = `
.sj-sheet-cell:focus { outline: 2px solid #2563eb; outline-offset: -2px; background: #fff; }
`;

const sheet: CSSProperties = { width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', background: '#fff', fontSize: '12px' };
const cell: CSSProperties = { border: '1px solid var(--border-strong, #cbd5e1)', padding: 0, height: '28px' };
const head: CSSProperties = {
  ...cell,
  background: 'var(--bg-hover, #f1f5f9)',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  padding: '4px 6px',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const no: CSSProperties = { textAlign: 'center', color: 'var(--text-muted)', fontSize: '11px', background: 'var(--bg-hover, #f8fafc)' };
const input: CSSProperties = { width: '100%', height: '100%', border: 'none', background: 'transparent', padding: '4px 6px', font: 'inherit', color: 'inherit' };
const remove: CSSProperties = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, width: '100%' };
